import axios from "axios";
import MessageReadStatus from "../readreceipts/MessageReadStatus";
import moment from "moment";
import React from "react";
import { AiOutlineFilePdf } from "react-icons/ai";
import { useQuery } from "react-query";
import { cloudFrontUrl, serverUrl } from "../../nestserver";
import MediaCarousel from "../media/MediaCarousel";

const SchoolAnnouncementFeed = () => {
  const fetchMessages = async () => {
    const res = await axios.get(
      serverUrl + `/personal-messages?type=school_announcements&limit=100&skip=0`,
      {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      },
    );
    return res.data;
  };
  const messagesQuery = useQuery(
    ["personal-messages", "school_announcements"],
    fetchMessages,
    {
      enabled: true,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
    },
  );

  const getMediaInfo = (item) => {
    let folder = "images";
    let mediaType = item?.type;

    if (item?.type) {
      if (item.type === "IMAGE") folder = "images";
      else if (item.type === "PDF") folder = "pdf";
      else if (item.type === "VIDEO") folder = "video";
      else if (item.type === "AUDIO" || item.type === "VOICE") folder = "audio";
    } else {
      const name = item?.name?.toLowerCase() || "";
      if (name.endsWith(".pdf")) {
        folder = "pdf";
        mediaType = "PDF";
      } else if (name.endsWith(".mp4")) {
        folder = "video";
        mediaType = "VIDEO";
      } else if (name.endsWith(".mp3")) {
        folder = "audio";
        mediaType = "AUDIO";
      } else if (name.endsWith(".wav") || name.endsWith(".webm")) {
        folder = "audio";
        mediaType = "VOICE";
      } else if (
        name.endsWith(".jpeg") ||
        name.endsWith(".jpg") ||
        name.endsWith(".png")
      ) {
        folder = "images";
        mediaType = "IMAGE";
      }
    }
    return { folder, mediaType };
  };

  const getMediaUrl = (item) => {
    const { folder } = getMediaInfo(item);
    return `${cloudFrontUrl}/${folder}/${item.id}`;
  };

  return (
    <>
      {messagesQuery?.data?.result?.data?.length > 0 ? (
        <div className="space-y-5">
          {messagesQuery?.data?.result?.data?.map((msg) => {
            const visualMedia = (msg.media || [])
              .filter((item) =>
                ["IMAGE", "VIDEO"].includes(getMediaInfo(item).mediaType),
              )
              .map((item) => ({ ...item, type: getMediaInfo(item).mediaType }));
            const audioMedia = (msg.media || []).filter(
              (item) => getMediaInfo(item).mediaType === "AUDIO",
            );
            const voiceMedia = (msg.media || []).filter(
              (item) => getMediaInfo(item).mediaType === "VOICE",
            );
            const pdfMedia = (msg.media || []).filter(
              (item) => getMediaInfo(item).mediaType === "PDF",
            );
            const classNames = (msg.class_names || []).filter(Boolean);

            return (
              <div
                key={msg._id}
                className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm transition-all hover:shadow-md"
              >
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="text-xl font-bold text-gray-800">
                        {msg.subject}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1">
                        <span className="text-sm font-medium text-gray-600">
                          {msg.sender_id?.first_name} {msg.sender_id?.last_name}
                        </span>
                        {msg.sender_id?.role && (
                          <span
                            style={{
                              backgroundColor: "rgba(20, 184, 166, 0.1)",
                              border: "1px solid rgba(20, 184, 166, 0.4)",
                              color: "#0d9488",
                            }}
                            className="text-xs font-semibold px-3 py-1 rounded-full"
                          >
                            Instructor
                          </span>
                        )}
                        {classNames.length > 0 && (
                          <span
                            style={{
                              backgroundColor: "rgba(99, 102, 241, 0.1)",
                              border: "1px solid rgba(99, 102, 241, 0.4)",
                              color: "#4f46e5",
                            }}
                            className="text-xs font-semibold px-3 py-1 rounded-full"
                          >
                            {classNames.join(", ")}
                          </span>
                        )}
                        <span className="text-gray-300">•</span>
                        <span className="text-xs text-gray-400">
                          {moment(msg.createdAt).format("DD MMM YYYY, hh:mm A")}
                        </span>
                        <MessageReadStatus
                          message={msg}
                          readInfoPath={`/personal-messages/${msg._id}/read-info`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="text-gray-700 leading-relaxed whitespace-pre-wrap border-t pt-4">
                    {msg.message}
                  </div>

                  {(visualMedia.length > 0 ||
                    audioMedia.length > 0 ||
                    voiceMedia.length > 0 ||
                    pdfMedia.length > 0) && (
                    <div className="space-y-4">
                      {visualMedia.length > 0 && (
                        <div className="mb-4">
                          <MediaCarousel
                            mediaItems={visualMedia}
                            getImageUrl={getMediaUrl}
                          />
                        </div>
                      )}

                      {audioMedia.map((item, index) => (
                        <div key={`audio-${index}`} className="max-w-md">
                          <div className="bg-linear-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-200 shadow-sm">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-2xl">🎵</span>
                              <span className="text-sm font-medium text-gray-700">
                                Audio File
                              </span>
                            </div>
                            <audio controls className="w-full">
                              <source
                                src={getMediaUrl(item)}
                                type="audio/mpeg"
                              />
                              Your browser does not support the audio element.
                            </audio>
                          </div>
                        </div>
                      ))}

                      {voiceMedia.map((item, index) => (
                        <div key={`voice-${index}`} className="max-w-md">
                          <div className="bg-linear-to-r from-blue-50 to-cyan-50 p-4 rounded-xl border border-blue-200 shadow-sm">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-2xl">🎙️</span>
                              <span className="text-sm font-medium text-gray-700">
                                Voice Message
                              </span>
                            </div>
                            <audio controls className="w-full">
                              <source src={getMediaUrl(item)} type="audio/wav" />
                              Your browser does not support the audio element.
                            </audio>
                          </div>
                        </div>
                      ))}

                      {pdfMedia.length > 0 && (
                        <div className="flex flex-wrap gap-4">
                          {pdfMedia.map((file, index) => (
                            <a
                              key={`pdf-${index}`}
                              href={getMediaUrl(file)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 border p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors max-w-60"
                            >
                              <div className="w-10 h-10 bg-red-50 rounded flex items-center justify-center text-red-500">
                                <AiOutlineFilePdf size={24} />
                              </div>
                              <div className="flex-1 min-width-0">
                                <div className="text-sm font-medium text-gray-700 truncate">
                                  {file.name}
                                </div>
                                <div className="text-[10px] text-gray-400">
                                  PDF Document
                                </div>
                              </div>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
          <p className="text-lg">No announcements yet</p>
          <p className="text-sm">
            Class announcements sent by instructors will appear here.
          </p>
        </div>
      )}
    </>
  );
};

export default SchoolAnnouncementFeed;
